import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  FileText,
  Layers,
  Boxes,
  Link as LinkIcon,
  Anchor,
  Wrench,
  ArrowUpRight,
} from "lucide-react";
import { Navbar } from "../components/site/Navbar";
import { Footer } from "../components/site/Footer";

const SERVICES = [
  {
    icon: FileText,
    title: "Shop Drawings",
    body: "Fabrication-ready drawings for beams, columns, braces and assemblies — dimensioned, pieced and ready for the shop floor.",
    deliverables: ["Fabrication drawings", "BOM & material lists", "CNC-ready data"],
  },
  {
    icon: Layers,
    title: "Erection Drawings",
    body: "Field-ready erection plans that keep crews moving — clear marks, elevations and installation sequencing.",
    deliverables: ["Member marks & elevations", "Bolt lists", "Sequencing plans"],
  },
  {
    icon: Boxes,
    title: "3D Modeling & BIM",
    body: "Coordinated Tekla and SDS2 models built for clash-free coordination with GC and MEP teams.",
    deliverables: ["Tekla / SDS2 models", "Clash detection", "IFC handover"],
  },
  {
    icon: LinkIcon,
    title: "Connection Design",
    body: "Engineered connections with calculations, backed by US-certified structural PEs.",
    deliverables: ["PE-stamped calcs", "Moment & shear connections", "Bracing connections"],
  },
  {
    icon: Anchor,
    title: "Anchor Bolt Plans",
    body: "Precise anchor bolt setting plans and templates that keep foundation work on schedule.",
    deliverables: ["Setting plans", "Embed templates", "Foundation coordination"],
  },
  {
    icon: Wrench,
    title: "Miscellaneous Steel",
    body: "Stairs, handrails, ladders, platforms and misc. metals detailed with fabrication in mind.",
    deliverables: ["Stairs & rails", "Ladders & platforms", "Lintels & embeds"],
  },
];

const PROCESS = [
  {
    step: "01",
    title: "Scope & RFQ review",
    body: "We review the bid set, specs and scope gaps, then return a fixed-fee quote within 24 hours.",
  },
  {
    step: "02",
    title: "Kickoff & model setup",
    body: "A dedicated FTP workspace opens; grids, codes and connection standards are locked with your team.",
  },
  {
    step: "03",
    title: "Modeling & connections",
    body: "Tekla / SDS2 modeling runs in parallel with engineered connection design — no idle handoffs.",
  },
  {
    step: "04",
    title: "QC & PE review",
    body: "Multi-stage checking on every sheet; connection packages stamped by US-certified PEs where required.",
  },
  {
    step: "05",
    title: "IFC delivery & support",
    body: "Issue-for-construction packages, revision-controlled drops, and field support across 3 working shifts.",
  },
];

const SECTORS = [
  "Residential",
  "Commercial",
  "Industrial",
  "Oil & Gas",
  "Airports",
  "Railway Stations",
];

export default function WhatWeDo() {
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen" data-testid="whatwedo-page">
      <Navbar />

      {/* Page hero */}
      <section
        data-testid="whatwedo-hero"
        className="relative min-h-[72vh] flex items-end overflow-hidden bg-[#0A2540]"
      >
        <div className="absolute inset-0">
          <img
            src="https://customer-assets-7cd3h4nn.emergentagent.net/job_akr-renders/artifacts/gc2btpum_BWb0K2rzLIcsYySPmwYYlaRGuWv5Ly2P3N_7vCx9sgZD03kkkWWJaXJyh9_cktnoKL7sD5GKr-wZoB5If7b4s95tTThKlEYUJc7OW3ooUB45h---BvmsWqZwae_XnwIBr-uHaFBSJh8xLdVPopydxxbiZnywR-NK9cvSNVRCwSI.jpg"
            alt="Ironworkers on a structural steel frame"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#081C33]/90 via-[#0A2540]/60 to-[#0A2540]/25 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#081C33]/85 via-transparent to-[#081C33]/45 pointer-events-none" />

        <div className="relative z-10 w-full px-6 md:px-10 lg:px-14 xl:px-16 pt-36 pb-16 md:pb-20">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="inline-flex items-center gap-2 mb-6 text-xs font-mono uppercase tracking-[0.22em] text-slate-300"
          >
            <span className="w-8 h-px bg-slate-400" />
            What we do
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="font-display font-semibold tracking-tight leading-[1.06] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white"
          >
            Steel detailing,
            <br />
            end to end.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.45 }}
            className="mt-6 max-w-2xl text-base md:text-lg text-slate-200/90 leading-relaxed"
          >
            From the first RFQ to the last bolt in the air — AKR Steel
            Detailing Services LLC produces the drawings, models and connection
            packages that fabricators and erectors build from.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-10 grid grid-cols-3 gap-4 max-w-md"
          >
            {[
              { k: "850+", v: "Projects delivered" },
              { k: "48K", v: "Tons / year" },
              { k: "3", v: "Working shifts" },
            ].map((s) => (
              <div key={s.v} className="border-l border-white/30 pl-4">
                <div className="font-display text-2xl md:text-3xl font-semibold text-white">
                  {s.k}
                </div>
                <div className="text-[10px] md:text-xs uppercase tracking-widest text-slate-300/80 font-mono mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="relative py-20 md:py-28 bg-white">
        <div className="px-6 md:px-10 lg:px-14 xl:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-14 lg:mb-16">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 mb-6 text-xs font-mono uppercase tracking-[0.22em] text-slate-500">
                <span className="w-8 h-px bg-[#0A2540]" />
                01 · Services
              </div>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold text-[#0A2540] tracking-tight leading-[1.08]">
                Six disciplines.{" "}
                <span className="text-slate-400">One accountable desk.</span>
              </h2>
            </div>
            <div className="lg:col-span-5 flex lg:items-end">
              <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-xl">
                Every service ships with defined deliverables — you always know
                exactly what lands in your inbox, and when.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                  data-testid={`whatwedo-service-${i}`}
                  className="group relative bg-slate-50/60 border border-slate-200 rounded-2xl p-7 md:p-8 hover:border-slate-400 hover:shadow-lg hover:shadow-slate-200/60 transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-12 h-12 rounded-md bg-[#0A2540] flex items-center justify-center">
                      <Icon className="w-5 h-5 text-white" strokeWidth={2} />
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400">
                      /{String(i + 1).padStart(2, "0")}
                    </div>
                  </div>
                  <h3 className="font-display font-semibold text-2xl text-[#0A2540] leading-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                    {s.body}
                  </p>
                  <div className="mt-6 pt-5 border-t border-slate-200 flex flex-wrap gap-2">
                    {s.deliverables.map((d) => (
                      <span
                        key={d}
                        className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-600 bg-white border border-slate-200 rounded-full px-2.5 py-1"
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="relative py-20 md:py-28 bg-[#0A2540] text-white overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-[0.06] pointer-events-none" />

        <div className="relative px-6 md:px-10 lg:px-14 xl:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-14 lg:mb-16">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 mb-6 text-xs font-mono uppercase tracking-[0.22em] text-slate-400">
                <span className="w-8 h-px bg-slate-500" />
                02 · How we work
              </div>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.08]">
                A process built for{" "}
                <span className="text-slate-300">zero surprises</span>.
              </h2>
            </div>
            <div className="lg:col-span-5 flex lg:items-end">
              <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-xl">
                Five stages, each with a defined output. You see progress
                weekly — not just at delivery.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
            {PROCESS.map((p, i) => (
              <motion.div
                key={p.step}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`whatwedo-step-${i}`}
                className="group relative border-t-2 border-white/15 hover:border-white/40 pt-6 transition-colors duration-300"
              >
                <div className="font-display text-4xl md:text-5xl font-semibold text-white/25 group-hover:text-white/70 transition-colors leading-none">
                  {p.step}
                </div>
                <h3 className="mt-4 font-display font-semibold text-lg leading-snug">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                  {p.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="relative py-20 md:py-24 bg-slate-50/60">
        <div className="px-6 md:px-10 lg:px-14 xl:px-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <div className="inline-flex items-center gap-2 mb-5 text-xs font-mono uppercase tracking-[0.22em] text-slate-500">
                <span className="w-8 h-px bg-[#0A2540]" />
                03 · Sectors we serve
              </div>
              <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-semibold text-[#0A2540] tracking-tight leading-[1.1]">
                Wherever steel goes up.
              </h2>
            </div>
            <a
              href="/#gallery"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A2540] hover:text-[#0F172A] transition-colors shrink-0 underline underline-offset-4"
            >
              Browse the structure gallery
              <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {SECTORS.map((s, i) => (
              <motion.div
                key={s}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                data-testid={`whatwedo-sector-${i}`}
                className="bg-white border border-slate-200 rounded-xl p-5 hover:border-slate-400 transition-colors duration-300"
              >
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 mb-2">
                  /{String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-display font-semibold text-base md:text-lg text-[#0A2540] leading-tight">
                  {s}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 md:py-28 bg-[#081C33] text-white overflow-hidden">
        <div className="relative px-6 md:px-10 lg:px-14 xl:px-16 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.08] max-w-3xl mx-auto"
          >
            Have a set of drawings{" "}
            <span className="text-slate-300">to detail?</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-5 text-slate-300 text-base md:text-lg max-w-xl mx-auto leading-relaxed"
          >
            Send your bid set or IFC package — our engineering team responds
            within one business day.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-9"
          >
            <a
              href="/#contact"
              data-testid="whatwedo-cta"
              className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-[#0A2540] font-semibold px-7 py-4 rounded-full transition-colors"
            >
              Start a project
              <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
